// src/features/goal/utils/objectiveUtils.ts
// Objective 相關的工具函數

import {
  Objective,
  ObjectiveCategory,
  OBJECTIVE_CATEGORY_CONFIG
} from '../types/objective';
import { Timestamp } from 'firebase/firestore';
import { format } from 'date-fns';
import { toDateObject } from '../../../utils/dateUtils';
import { mapLegacyCategory } from '../config/categoryMapping';

/**
 * 計算 Objective 的視覺進度（浮動門檻系統）
 *
 * 規則：
 * - 進度條顯示 0-75%（永不到 100%）
 * - Tier 0: 0-75 Ripples，1 格 = 1 Ripple → 0%-75%
 * - 達到 75 Ripples 時觸發 tier 升級
 * - Tier 1: 75-112.5 Ripples，1 格 = 1.5 Ripples → 50%-75%
 * - 再達到 75 格時再次升級
 * - Tier 2: 112.5-168.75 Ripples，1 格 = 2.25 Ripples → 50%-75%
 * - 以此類推...
 *
 * 視覺效果：
 * - 升級時進度從 75% 跳到 50%（固定降幅 25%）
 * - 確保永遠不會達到或超過 75%
 * - Ripples 持續累積，只是每格代表的價值不同
 *
 * 數學原理：
 * - Tier N 的門檻 = 75 * (1.5^N) Ripples
 * - 當前進度 = 總 Ripples / 當前每格價值
 * - 例如：100 Ripples 在 Tier 1（每格 1.5）= 100/1.5 ≈ 66.7%
 *
 * @param ripples 累積的 Ripples 值
 * @returns 視覺進度百分比 (0-75)
 *
 * @example
 * calculateObjectiveProgress(0)      // → 0%
 * calculateObjectiveProgress(37.5)   // → 37.5%
 * calculateObjectiveProgress(75)     // → 75%
 * calculateObjectiveProgress(75.01)  // → 50% (tier 升級)
 * calculateObjectiveProgress(112.5)  // → 75%
 * calculateObjectiveProgress(112.51) // → 50% (tier 升級)
 */
export function calculateObjectiveProgress(ripples: number): number {
  if (ripples === 0) return 0;

  const MULTIPLIER = 1.5;

  // 當前每格代表多少 Ripples
  let valuePerGrid = 1;

  // 計算當前 tier（每格的價值）
  let thresholdForNextTier = 75;  // 第一次升級需要 75 Ripples

  while (ripples >= thresholdForNextTier) {
    valuePerGrid *= MULTIPLIER;
    // 下一次升級需要額外累積 75 格 = 75 * valuePerGrid Ripples
    thresholdForNextTier += 75 * valuePerGrid;
  }

  // 當前進度 = 總 Ripples / 每格價值
  const currentProgress = ripples / valuePerGrid;

  // 確保不超過 75%，並四捨五入到小數點後 1 位
  return Math.min(Math.round(currentProgress * 10) / 10, 75);
}

/**
 * 計算下一個 tier 還需要多少 Ripples
 *
 * @param ripples 當前累積的 Ripples 值
 * @returns 距離下一個 tier 升級還需要的 Ripples 數量
 *
 * @example
 * getRipplesUntilNextTier(50)    // → 25 (到 75)
 * getRipplesUntilNextTier(75)    // → 37.5 (到 112.5)
 * getRipplesUntilNextTier(100)   // → 12.5 (到 112.5)
 */
export function getRipplesUntilNextTier(ripples: number): number {
  const MULTIPLIER = 1.5;

  let valuePerGrid = 1;
  let thresholdForNextTier = 75;

  // 找到當前 tier
  while (ripples >= thresholdForNextTier) {
    valuePerGrid *= MULTIPLIER;
    thresholdForNextTier += 75 * valuePerGrid;
  }

  return Math.ceil(thresholdForNextTier - ripples);
}

/**
 * 獲取當前 tier 的詳細資訊
 * （用於 debug 或顯示額外資訊）
 *
 * @param ripples 當前累積的 Ripples 值
 * @returns tier 相關資訊
 *
 * @example
 * getObjectiveTierInfo(100)
 * // → {
 * //   tier: 1,
 * //   valuePerGrid: 1.5,
 * //   ripplesInCurrentTier: 25,
 * //   currentTierThreshold: 37.5,
 * //   nextTierAt: 112.5
 * // }
 */
export function getObjectiveTierInfo(ripples: number) {
  const MULTIPLIER = 1.5;

  let tier = 0;
  let valuePerGrid = 1;
  let totalRipplesForCurrentTier = 0;
  let thresholdForNextTier = 75;

  while (ripples >= thresholdForNextTier) {
    tier++;
    totalRipplesForCurrentTier = thresholdForNextTier;
    valuePerGrid *= MULTIPLIER;
    thresholdForNextTier += 75 * valuePerGrid;
  }

  return {
    tier,                                               // 當前所在的 tier (0, 1, 2, ...)
    valuePerGrid,                                       // 當前每格代表多少 Ripples
    ripplesInCurrentTier: ripples - totalRipplesForCurrentTier,  // 在當前 tier 已累積的 Ripples
    currentTierThreshold: thresholdForNextTier - totalRipplesForCurrentTier,  // 當前 tier 的總門檻
    nextTierAt: thresholdForNextTier,                   // 下一個 tier 的起點
  };
}

/**
 * 按狀態分組 Objectives
 *
 * @param objectives 所有 objectives
 * @returns 按狀態分組的對象
 *
 * @example
 * const { active, paused } = groupObjectivesByStatus(objectives);
 * // active: 所有 status='active' 的目標
 * // paused: 所有 status='paused' 的目標
 */
export function groupObjectivesByStatus(objectives: Objective[]) {
  return {
    active: objectives.filter(obj => obj.status === 'active'),
    paused: objectives.filter(obj => obj.status === 'paused'),
    completed: objectives.filter(obj => obj.status === 'completed'),
    archived: objectives.filter(obj => obj.status === 'archived'),
  };
}

/**
 * 驗證 Objective 標題格式
 *
 * @param title 標題字串
 * @returns 驗證結果 { valid: boolean; error?: string }
 */
export function validateObjectiveTitle(title: string): {
  valid: boolean;
  error?: string;
} {
  const trimmed = title.trim();

  if (!trimmed) {
    return { valid: false, error: 'Title is required' };
  }

  if (trimmed.length < 3) {
    return { valid: false, error: 'Title must be at least 3 characters' };
  }

  if (trimmed.length > 80) {
    return { valid: false, error: 'Title must be at most 80 characters' };
  }

  return { valid: true };
}

/**
 * 獲取 category 配置（向後兼容）
 *
 * Apple 風格設計：
 * - 如果沒有 category，預設使用 'growth'（取代舊的 'personal'）
 * - 自動處理舊 category 值（learning → growth, personal → growth, creative → leisure）
 * - 確保所有 objectives 都有對應的 icon
 *
 * @param category Objective 的 category（可能為 undefined）
 * @returns Category 配置物件（包含 label 和 icon）
 *
 * @example
 * getCategoryConfig('work')      // → { label: 'Work', icon: Briefcase }
 * getCategoryConfig('learning')  // → { label: 'Growth', icon: GraduationCap } (自動映射)
 * getCategoryConfig(undefined)   // → { label: 'Growth', icon: GraduationCap }
 */
export function getCategoryConfig(category?: ObjectiveCategory) {
  // 使用統一的映射邏輯處理 legacy category
  const mappedCategory = mapLegacyCategory(category, { logMapping: false });

  // 返回對應的配置
  return OBJECTIVE_CATEGORY_CONFIG[mappedCategory];
}

/**
 * 格式化開始日期（Apple 風格：簡潔）
 *
 * 規則：
 * - 如果是今天：顯示 "Today"
 * - 其他：顯示 "Oct 29" 格式
 *
 * 修復記錄 (2025-10-29):
 * - 使用 toDateObject() 工具函數處理多種日期格式
 * - 修復 "startDate.toDate is not a function" 錯誤
 *
 * @param startDate Objective 的開始日期（支援 Timestamp、Date 等多種格式）
 * @returns 格式化後的日期字串
 *
 * @example
 * formatStartDate(todayTimestamp)  // → "Today"
 * formatStartDate(someTimestamp)   // → "Oct 29"
 */
export function formatStartDate(startDate: Timestamp | Date | number): string {
  // 使用安全的日期轉換函數（處理多種格式）
  const date = toDateObject(startDate);
  const now = new Date();

  // 計算天數差異
  const diffDays = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
  );

  // 如果是今天，顯示 "Today"
  if (diffDays === 0) {
    return 'Today';
  }

  // 否則顯示日期（Oct 29 格式）
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * 按完成月份分組目標（用於 Achievements/Memento 頁面）
 *
 * 設計理念：
 * - 將完成的目標按月份分組，營造「時間收藏」的感覺
 * - 月份格式採用簡寫：Dec '25（國際化且簡潔）
 * - 結果按時間降序排列（最新的月份在最前面）
 *
 * @param objectives 已完成的 Objectives 陣列
 * @returns Map<string, Objective[]>，key 為月份標題（如 "Dec '25"）
 *
 * @example
 * const grouped = groupObjectivesByMonth(completedObjectives);
 * // Map {
 * //   "Dec '25" => [obj1, obj2],
 * //   "Nov '25" => [obj3],
 * //   "Oct '25" => [obj4, obj5, obj6]
 * // }
 */
export function groupObjectivesByMonth(objectives: Objective[]): Map<string, Objective[]> {
  const groups = new Map<string, Objective[]>();

  // 先按完成時間降序排序
  const sortedObjectives = [...objectives].sort((a, b) => {
    if (!a.completedAt || !b.completedAt) return 0;
    return b.completedAt.seconds - a.completedAt.seconds;
  });

  sortedObjectives.forEach(obj => {
    if (!obj.completedAt) return;

    const date = toDateObject(obj.completedAt);
    // 格式：Dec '25
    const monthKey = format(date, "MMM ''yy");

    if (!groups.has(monthKey)) {
      groups.set(monthKey, []);
    }
    groups.get(monthKey)!.push(obj);
  });

  return groups;
}
